<script lang="ts">
	// The owner dashboard shell, as the Owner Dashboard - Setup, Menu and Sales
	// pages draw it: the rail at the inline start, with the restaurant, the sections and their
	// counts, and Sign out (the theme lives in Settings); the page beside it, under a
	// title and its one action.
	//
	// Below 768 px the rail becomes a bar across the top, and the sections
	// fold behind a button that names the one on screen.
	//
	// The shell also draws the dashboard's two passing messages, the Undo and
	// the flash, so a page can leave while its Undo is still counting, and
	// its two standing ones above the page: a Bosla admin's visit (UC-64) and
	// the subscription warning (UC-24).

	import Band from '../components/Band.svelte';
	import Loading from '../components/Loading.svelte';
	import Tables from '../panels/Tables.svelte';
	import Drivers from '../panels/Drivers.svelte';
	import { shopPath, ownerLocation, type ShopSection } from '../owner/navigation';
	import BrandMark from '../components/BrandMark.svelte';
	import Button from '../components/Button.svelte';
	import NavItem from '../components/NavItem.svelte';
	import Notice from '../components/Notice.svelte';
	import Sheet from '../components/Sheet.svelte';
	import { whatsappHref, whatsappLabel } from '../contact';
	import { subscriptionLine } from '../owner/subscription';
	import type { IconName } from '../components/Icon.svelte';
	import Home from '../panels/Home.svelte';
	import Shops from '../panels/Shops.svelte';
	import Tills from '../panels/Tills.svelte';
	import Cashiers from '../panels/Cashiers.svelte';
	import Menu from '../panels/Menu.svelte';
	import Publish from '../panels/Publish.svelte';
	import Discounts from '../panels/Discounts.svelte';
	import OwnerOrders from '../panels/OwnerOrders.svelte';
	import Reports from '../panels/Reports.svelte';
	import Shifts from '../panels/Shifts.svelte';
	import Customers from '../panels/Customers.svelte';
	import Settings from '../panels/Settings.svelte';
	import Catalogue from '../panels/Catalogue.svelte';
	import SignInMethods from '../panels/SignInMethods.svelte';
	import Help from '../panels/Help.svelte';
	import { fill, isArabic, message, type Language, type MessageKey } from '../i18n/messages';
	import { formatClock } from '../format/time';
	import { currentPath, navigate } from '../router.svelte';
	import { signOut, type Account } from '../owner/session.svelte';
	import {
		discountEditing,
		forgetOwnerData,
		loadShops,
		loadCashiers,
		loadDiscounts,
		loadTills,
		ownerShops,
		ownerDiscounts,
	} from '../owner/data.svelte';
	import { clearFlash, commitNow, currentFlash, pendingUndo, undo } from '../owner/notices.svelte';

	interface Props {
		language: Language;
		account: Account;
	}

	let { language, account }: Props = $props();

	interface Pane {
		path: string;
		key: MessageKey;
		icon: IconName;
		/** The page's one action in its title row, if it has one. */
		action?: MessageKey;
		count?: () => number;
	}

	const home: Pane = { path: '/owner', key: 'ownerHome', icon: 'today' };
	const businessPanes: Pane[] = [
		home,
		{ path: '/owner/menu', key: 'ownerMenu', icon: 'tag' },
		{
			path: '/owner/discounts',
			key: 'ownerDiscounts',
			icon: 'percent',
			action: 'discountsAdd',
			count: () => ownerDiscounts().value.length,
		},
		{ path: '/owner/orders', key: 'ownerOrders', icon: 'receipt' },
		{ path: '/owner/reports', key: 'ownerReports', icon: 'chart' },
		{ path: '/owner/shifts', key: 'ownerShifts', icon: 'drawer' },
		{ path: '/owner/customers', key: 'ownerCustomers', icon: 'phone' },
		{ path: '/owner/catalogue', key: 'ownerPublicPage', icon: 'book' },
		{
			path: '/owner/shops',
			key: 'ownerShops',
			icon: 'store',
			action: 'shopsAdd',
			count: () => ownerShops().value.length,
		},
	];
	const utilities: Pane[] = [
		{ path: '/owner/settings', key: 'ownerSettings', icon: 'settings' },
		{ path: '/owner/help', key: 'ownerHelp', icon: 'play' },
	];
	const location = $derived(ownerLocation(currentPath()));
	const shop = $derived(
		location.kind === 'shop' ? ownerShops().value.find((s) => s.id === location.shopId) : undefined,
	);
	const shopId = $derived(location.kind === 'shop' ? location.shopId : '');
	const shopMode = $derived(location.kind === 'shop');
	const missingShop = $derived(
		location.kind === 'missing' ||
			(shopMode && ownerShops().loaded && !ownerShops().failure && !shop),
	);
	const shopTasks: {
		section: ShopSection;
		key: MessageKey;
		icon: IconName;
		action?: MessageKey;
	}[] = [
		{ section: 'overview', key: 'ownerHome', icon: 'today' },
		{ section: 'cashiers', key: 'ownerCashiers', icon: 'user', action: 'cashiersAdd' },
		{ section: 'tills', key: 'ownerTills', icon: 'device', action: 'tillsAdd' },
		{ section: 'tables', key: 'tablesTitle', icon: 'store' },
		{ section: 'drivers', key: 'driversTitle', icon: 'send' },
		{ section: 'delivery', key: 'ownerDelivery', icon: 'store' },
		{ section: 'catalogue', key: 'ownerShopVisibility', icon: 'book' },
		{ section: 'settings', key: 'ownerShopSettings', icon: 'settings' },
	];
	const panes: Pane[] = $derived(
		shopMode && !missingShop
			? shopTasks.map((task) => ({ ...task, path: shopPath(shopId, task.section) }))
			: businessPanes,
	);
	const onMethods = $derived(
		location.kind === 'business' && location.section === 'sign-in-methods',
	);
	const legacyPanes: Pane[] = [
		{ path: '/owner/cashiers', key: 'ownerCashiers', icon: 'user', action: 'cashiersAdd' },
		{ path: '/owner/tills', key: 'ownerTills', icon: 'device', action: 'tillsAdd' },
	];
	const active: Pane = $derived(
		[...panes, ...utilities, ...legacyPanes].find(
			(p) =>
				p.path ===
				(location.kind === 'shop'
					? shopPath(location.shopId, location.section)
					: onMethods
						? '/owner/settings'
						: currentPath()),
		) ??
			(shopMode
				? {
						path: currentPath(),
						key:
							location.kind === 'shop' && location.section === 'reports'
								? 'ownerReports'
								: 'ownerOrders',
						icon: 'today' as IconName,
					}
				: home),
	);
	const section = $derived(location.kind === 'missing' ? '' : location.section);
	const shops = $derived(ownerShops().value);
	const noShops = $derived(ownerShops().loaded && shops.length === 0);

	// The rail counts shops, cashiers, tills and discounts, so it reads them
	// once here.
	$effect(() => {
		const fallback = message(language, 'loadFailed');
		void loadShops(fallback);
		void loadCashiers(fallback);
		void loadTills(fallback);
		void loadDiscounts(fallback);
	});

	// Folded behind a button on a narrow screen.
	let menuOpen = $state(false);

	// A page's title-row action asks the page to open its form. The page
	// watches the count, so a second press opens it again.
	let actionAsked = $state(0);

	// Browser Back/Forward also leave the current task. Do not replay an
	// earlier page's Add request when a panel mounts at the destination.
	$effect(() => {
		currentPath();
		actionAsked = 0;
		menuOpen = false;
	});

	// The catalogue's "Address in Shops" opens that shop in Shops.
	let returnTo = $state('');
	const returnLabel = $derived(returnTo ? message(language, 'ownerReturnToTask') : '');

	function go(path: string) {
		menuOpen = false;
		actionAsked = 0;

		navigate(path);
	}

	function openShop(id: string) {
		returnTo = currentPath();
		go(shopPath(id, 'settings'));
	}

	async function leave() {
		await commitNow();
		await signOut();
		forgetOwnerData();
		navigate('/owner');
	}

	// The subscription warning (UC-24): on every section while the server
	// says there is a notice, and the notice in a sheet when it is tapped.
	const subscription = $derived(account.subscription);
	const warning = $derived(subscriptionLine(language, subscription));
	let noticeOpen = $state(false);

	// A Bosla admin's visit (UC-64): a standing notice names the restaurant
	// and when the visit ends, on every section, with Exit, which signs out
	// as the rail's Sign out does. The owner's own session has no visit, so
	// the owner never sees it.
	const visit = $derived(account.visit);
	const visitLine = $derived(
		visit
			? fill(message(language, 'visitViewing'), account.name, formatClock(visit.endsAt, language))
			: '',
	);
</script>

<div class="frame">
	{#if visit}
		<Notice
			life="standing"
			tone="flag"
			action={message(language, 'visitExit')}
			onAction={() => void leave()}
			actionTestId="visit-exit"
			testId="visit-notice"
		>
			{visitLine}
		</Notice>
	{/if}
	{#if warning}
		<Notice
			life="standing"
			tone="flag"
			action={message(language, 'subscriptionHowToRenew')}
			onAction={() => (noticeOpen = true)}
			actionTestId="subscription-how"
			testId="subscription-warning"
		>
			{warning}
		</Notice>
	{/if}
	<div class="dashboard" class:open={menuOpen} data-testid="screen-dashboard">
		<aside>
			<div class="brand">
				<BrandMark size={26} />
				<div class="names">
					<span class="name" data-testid="dashboard-restaurant">{shop?.name ?? account.name}</span>
					<span class="count" data-testid="dashboard-context">
						{#if shopMode}{account.name}{:else}
							<bdi class="n" dir="ltr">{shops.length}</bdi>
							{message(language, 'ownerShopsCount')}
						{/if}
					</span>
				</div>
				<span class="toggle">
					<Button
						testId="dashboard-menu"
						label={message(language, active.key)}
						icon="expand"
						tone="secondary"
						size="sm"
						onPress={() => (menuOpen = !menuOpen)}
					/>
				</span>
			</div>
			<nav data-testid="dashboard-nav">
				{#if shopMode || missingShop}
					<div class="context">
						<Button
							testId="nav-business"
							label={account.name}
							icon="back"
							mirrorIcon={!isArabic(language)}
							tone="text"
							size="sm"
							onPress={() => go('/owner')}
						/>
						<Button
							testId="nav-shop-picker"
							label={message(language, 'ownerShops')}
							tone="text-quiet"
							size="sm"
							onPress={() => go('/owner/shops')}
						/>
					</div>
				{/if}
				{#each panes as pane (pane.path)}
					<NavItem
						testId={`nav-${pane.path === '/owner' || (shopMode && pane.path.endsWith('/overview')) ? 'home' : pane.path.split('/').pop() || 'home'}`}
						label={message(language, pane.key)}
						icon={pane.icon}
						meta={pane.count ? String(pane.count()) : undefined}
						active={pane.path === active.path}
						onPress={() => go(pane.path)}
					/>
				{/each}
				<!-- Account settings and Help are the business's; a shop's rail
				     holds its own tasks alone (owner, 2026-10-17). -->
				{#if !shopMode}
					{#each utilities as pane (pane.path)}
						<NavItem
							testId={`nav-${pane.path.split('/').pop()}`}
							label={message(
								language,
								pane.path === '/owner/settings' ? 'ownerAccountSettings' : pane.key,
							)}
							icon={pane.icon}
							active={pane.path === active.path}
							onPress={() => go(pane.path)}
						/>
					{/each}
				{/if}
			</nav>
			<div class="foot">
				<Button
					testId="sign-out"
					label={message(language, 'ownerSignOut')}
					tone="text-quiet"
					size="sm"
					onPress={() => void leave()}
				/>
			</div>
		</aside>

		<main data-testid="dashboard-pane">
			<div class="page">
				<div class="title">
					<h1>{onMethods ? message(language, 'methodsTitle') : message(language, active.key)}</h1>
					<span class="grow"></span>
					{#if active.action}
						<Button
							testId="page-action"
							icon="plus"
							label={message(language, active.action)}
							tone="ink"
							size="sm"
							disabled={missingShop ||
								(shopMode && !shop) ||
								(active.path !== '/owner/shops' && noShops) ||
								(active.path === '/owner/discounts' && discountEditing())}
							onPress={() => actionAsked++}
						/>
					{/if}
				</div>

				{#if returnTo && returnTo !== currentPath()}
					<Button
						testId="owner-return"
						label={returnLabel}
						icon="back"
						tone="text"
						size="sm"
						onPress={() => {
							const path = returnTo;
							returnTo = '';
							go(path);
						}}
					/>
				{/if}
				{#if shopMode && !ownerShops().loaded}
					<Loading label={message(language, 'loading')} />
				{:else if missingShop}
					<Band tone="flag" testId="shop-missing">{message(language, 'ownerShopMissing')}</Band>
					<Button
						testId="shop-missing-back"
						label={message(language, 'ownerShops')}
						onPress={() => go('/owner/shops')}
					/>
				{:else if shopMode && !shop}
					<Band
						tone="flag"
						testId="shop-load-failed"
						action={message(language, 'retry')}
						actionTestId="shop-retry"
						onAction={() => void loadShops(message(language, 'loadFailed'))}
						>{ownerShops().failure}</Band
					>
				{:else}
					{#if section === 'menu' || section === 'discounts'}
						<Publish {language} {account} />
					{/if}
					{#key `${shopId}:${section}`}
						{#if section === 'home' || section === 'overview'}
							<Home
								{language}
								{account}
								{shopId}
								onOrders={(id) => go(shopPath(id, 'orders'))}
								onShop={(id) => go(shopPath(id))}
							/>
							{#if shopMode}<Button
									testId="shop-report"
									label={message(language, 'ownerReports')}
									icon="chart"
									tone="secondary"
									onPress={() => go(shopPath(shopId, 'reports'))}
								/>{/if}
						{:else if section === 'shops'}
							<Shops
								{language}
								{account}
								{actionAsked}
								view="list"
								onOpenShop={(id) => go(shopPath(id))}
							/>
							<div class="legacy-index">
								<Button
									testId="all-cashiers"
									label={message(language, 'ownerAllCashiers')}
									tone="text"
									onPress={() => go('/owner/cashiers')}
								/>
								<Button
									testId="all-tills"
									label={message(language, 'ownerAllTills')}
									tone="text"
									onPress={() => go('/owner/tills')}
								/>
							</div>
						{:else if section === 'tills'}
							{#if !shopMode}<Band testId="all-tills-scope"
									>{message(language, 'ownerAllTills')}</Band
								>{/if}
							<Tills {language} {actionAsked} {shopId} timeZone={account.settings.timezone} />
						{:else if section === 'cashiers'}
							{#if !shopMode}<Band testId="all-cashiers-scope"
									>{message(language, 'ownerAllCashiers')}</Band
								>{/if}
							<Cashiers {language} {actionAsked} {shopId} />
						{:else if section === 'tables'}
							<Tables {language} {shopId} />
						{:else if section === 'drivers'}
							<Drivers {language} {shopId} />
						{:else if section === 'receipt'}
							<!-- An old link to a shop's receipt words: they are shared now (#620). -->
							<Band testId="printing-moved">{message(language, 'printingMoved')}</Band>
							<Button
								testId="printing-moved-open"
								label={message(language, 'printingOpenSettings')}
								tone="secondary"
								onPress={() => go('/owner/settings')}
							/>
						{:else if section === 'delivery' || (section === 'settings' && shopMode)}
							<Shops
								{language}
								{account}
								actionAsked={0}
								{shopId}
								view={section === 'delivery' ? 'delivery' : 'settings'}
							/>
						{:else if section === 'menu'}
							<Menu {language} {account} />
						{:else if section === 'discounts'}
							<Discounts {language} minorDigits={account.settings.minorDigits} {actionAsked} />
						{:else if section === 'orders'}
							<OwnerOrders {language} {account} {shopId} />
						{:else if section === 'shifts'}
							<Shifts {language} {account} />
						{:else if section === 'reports'}
							<Reports {language} {account} {shopId} />
						{:else if section === 'customers'}
							<Customers {language} minorDigits={account.settings.minorDigits} />
						{:else if section === 'catalogue'}
							<Catalogue {language} {shopId} onShopAddress={openShop} />
							{#if shopMode}<Button
									testId="shop-public-page"
									label={message(language, 'ownerOpenPublicSettings')}
									tone="secondary"
									onPress={() => {
										returnTo = currentPath();
										go('/owner/catalogue');
									}}
								/>{/if}
						{:else if onMethods}
							<SignInMethods {language} />
						{:else if section === 'settings'}
							<Settings {language} settings={account.settings} />
						{:else if section === 'help'}
							<Help {language} />
						{/if}
					{/key}
				{/if}
			</div>
		</main>

		{#if pendingUndo()}
			{#key pendingUndo()}
				<Notice
					life="deadline"
					tone="neutral"
					action={message(language, 'sellUndo')}
					actionTestId="undo"
					onAction={undo}
					onDismiss={() => void commitNow()}
					testId="undo-notice"
				>
					{pendingUndo()?.text}
				</Notice>
			{/key}
		{/if}
		{#if currentFlash()}
			{#key currentFlash()}
				<Notice life="flash" tone="ok" onDismiss={clearFlash} testId="flash">
					{currentFlash()}
				</Notice>
			{/key}
		{/if}
	</div>
</div>

<Sheet
	open={noticeOpen && warning !== ''}
	title={message(language, 'subscriptionTitle')}
	onClose={() => (noticeOpen = false)}
	closeLabel={message(language, 'subscriptionClose')}
	testId="subscription-notice"
>
	<div class="notice">
		<p class="line">{warning}</p>
		<div class="contact">
			<span class="label">{message(language, 'subscriptionContact')}</span>
			{#if subscription.supportEmail}
				<Button
					testId="subscription-email"
					label={subscription.supportEmail}
					href="mailto:{subscription.supportEmail}"
					tone="text"
					size="md"
					mono
				/>
			{/if}
			{#if subscription.supportWhatsapp}
				<Button
					testId="subscription-whatsapp"
					label={whatsappLabel(
						message(language, 'suspendedWhatsapp'),
						subscription.supportWhatsapp,
					)}
					href={whatsappHref(subscription.supportWhatsapp)}
					newTab
					tone="text"
					size="md"
				/>
			{/if}
		</div>
		<p class="only">{message(language, 'subscriptionOnlyBosla')}</p>
	</div>
</Sheet>

<style>
	/* The subscription warning sits above the whole dashboard, in the flow,
	   so it pushes the page down rather than covering it. */
	.frame {
		min-block-size: 100dvh;
		display: flex;
		flex-direction: column;
	}

	.notice {
		max-inline-size: var(--measure);
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.notice .line {
		margin: 0;
		font-size: var(--text-md);
		font-weight: 700;
		line-height: var(--leading-snug);
		color: var(--text-strong);
		text-wrap: pretty;
	}

	.notice .contact {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--space-1);
	}

	.notice .label {
		font-size: var(--text-sm);
		color: var(--text-body);
	}

	.notice .only {
		margin: 0;
		font-size: var(--text-2xs);
		line-height: var(--leading-snug);
		color: var(--text-muted);
	}

	/* The rail's width is where a floating Notice starts, as the design's
	   shell sets it, so a flash centres over the page and not the rail. */
	.dashboard {
		--notice-inset-start: var(--sidebar-width);
		flex: 1;
		display: flex;
		flex-wrap: wrap;
		align-items: stretch;
		background: var(--surface-page);
		color: var(--text-strong);
	}

	aside {
		flex: none;
		inline-size: var(--sidebar-width);
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
		padding: var(--space-9) 0;
		background: var(--surface-page);
		border-inline-end: var(--border-width) solid var(--border-default);
	}

	.brand {
		display: flex;
		align-items: center;
		gap: var(--space-4);
		padding: 0 var(--space-5);
	}

	.names {
		flex: 1;
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
	}

	.name {
		font-size: var(--text-md);
		font-weight: 700;
		overflow-wrap: anywhere;
	}

	.count {
		font-size: var(--text-micro);
		color: var(--text-muted);
	}

	.n {
		font-family: var(--font-mono);
	}

	.toggle {
		display: none;
	}

	nav {
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
		padding: 0 var(--space-3);
	}

	.context {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: space-between;
		gap: var(--space-2);
		padding-block-end: var(--space-2);
		margin-block-end: var(--space-2);
		border-block-end: var(--border-width) solid var(--border-default);
	}

	/* A long restaurant name wraps inside the rail rather than past it. */
	.context :global([data-testid='nav-business']) {
		min-width: 0;
		white-space: normal;
		text-align: start;
	}

	.legacy-index {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-5);
	}

	.foot {
		display: flex;
		gap: var(--space-3);
		align-items: center;
		padding: var(--space-5) var(--space-5) 0;
		margin-block-start: auto;
	}

	main {
		flex: 999 1 520px;
		min-inline-size: 0;
		padding: var(--page-pad) clamp(16px, 3vw, 26px);
	}

	.page {
		max-inline-size: 1080px;
		inline-size: 100%;
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	.title {
		display: flex;
		align-items: center;
		gap: var(--space-5);
		flex-wrap: wrap;
	}

	h1 {
		margin: 0;
		font-size: var(--text-h3);
		font-weight: 700;
		letter-spacing: var(--tracking-tight);
		line-height: var(--leading-headline);
	}

	.grow {
		flex: 1;
	}

	/* A narrow screen: the rail is a bar across the top, and the sections
	   fold behind the button that names the one on screen. */
	@media (max-width: 767px) {
		.dashboard {
			--notice-inset-start: 0px;
		}

		aside {
			flex: 1 1 100%;
			inline-size: auto;
			gap: var(--space-5);
			padding: var(--space-5) var(--space-3);
			border-inline-end: none;
			border-block-end: var(--border-width) solid var(--border-default);
		}

		.toggle {
			display: block;
		}

		nav,
		.foot {
			display: none;
		}

		.open nav,
		.open .foot {
			display: flex;
		}

		.foot {
			margin-block-start: 0;
		}
	}
</style>
